<script lang="ts" setup>
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import { filter, get, includes, intersection, isString, size } from 'lodash'
import HELM_NAV from '@helm/constants/mainNav'
import type { HelmNavItem } from '@helm/types/helmMainNav'
import useMe from '@helm/composables/queries/me'
import { mainNavDestination } from '@helm/utils/sideNav'
import type { SideNavLocation } from '@helm/types/sideNav'

const route = useRoute()

// Determine if an url path matches the current page path, including react page equivalents
// A tab is also current on every page of its side nav, wherever those pages live (Roles is at /roles).
const isCurrentRoute = (path: string, name: string | undefined, sideNav: SideNavLocation[] = []) => {
    const current = get(route.meta, `props.sideNav`) as SideNavLocation | undefined
    return (
        Boolean(current && includes(sideNav, current)) ||
        route.path.startsWith(path) ||
        (name && isString(route.name) && route.name.startsWith(name))
    )
}

// Get first segment of url path, not including react prefix

const primaryLinkClasses = (path = ``, name: string | undefined, sideNav: SideNavLocation[] = []) => [
    `inline-block`,
    `flex`,
    `items-center`,
    `text-base`,
    `font-medium`,
    `rounded-full`,
    `!py-0`,
    `!px-3`,
    `!mx-2`,
    `h-9`,
    `leading-none`,
    `whitespace-nowrap`,
    `transition-colors`,
    `hover:no-underline`,
    `hover:!text-primary`,
    `hover:!bg-primary-50`,
    ...(isCurrentRoute(path, name, sideNav) ? [`text-primary`, `bg-primary-50`] : [`text-primary-50`, `bg-transparent`])
]

const { data: me, permissions } = useMe()

const filteredNav = computed(() =>
    filter(HELM_NAV, (item: HelmNavItem) => {
        if (item.permissions && !size(intersection(item.permissions, permissions.value))) {
            return false
        }

        if (item.commandCenterLinked && !me.value?.hasCommandCenter) {
            return false
        }

        return true
    })
)

const destination = (item: HelmNavItem) => mainNavDestination(item, permissions.value)
</script>

<template>
    <ul class="flex flex-auto flex-nowrap items-center overflow-auto">
        <li
            v-for="item in filteredNav"
            :key="item.path">
            <a
                v-if="item.target"
                :href="item.path"
                :target="item.target"
                :class="primaryLinkClasses(item.path, item.name, item.sideNav)">
                {{ item.label }}
            </a>
            <router-link
                v-else
                :to="destination(item)"
                :class="primaryLinkClasses(item.path, item.name, item.sideNav)">
                {{ item.label }}
            </router-link>
        </li>
    </ul>
</template>

<style lang="scss">
.v-popper--theme-main-nav-more {
    .v-popper {
        &__wrapper {
            @apply overflow-hidden rounded bg-white shadow-dropdown;
        }

        &__arrow-container {
            @apply hidden;
        }
    }
}
</style>
